import React from 'react';
import { Box, MenuItem, Pagination, PaginationItem, Select, Typography, useTheme } from '@mui/material';

interface DataTablePaginationProps {
    /** Total number of pages */
    totalPages: number;
    /** Current page (1-indexed) */
    currentPage: number;
    /** Items per page */
    pageSize: number;
    /** Available page size options */
    pageSizeOptions?: number[];
    /** Callback when page changes */
    onPageChange: (page: number) => void;
    /** Callback when page size changes */
    onPageSizeChange: (pageSize: number) => void;
    /** Enable dense/compact styling */
    dense?: boolean;
}

/**
 * DataTable Pagination Component
 * Provides pagination controls for the DataTable component
 */
export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
    totalPages,
    currentPage,
    pageSize,
    pageSizeOptions = [10, 20, 50, 100],
    onPageChange,
    onPageSizeChange,
    dense = false,
}) => {
    const theme = useTheme();

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        // Prevent going before first page or after last page
        if (value < 1 || value > totalPages) return;
        onPageChange(value);
    };

    const handlePageSizeChange = (event: any) => {
        onPageSizeChange(event.target.value);
    };

    return (
        <Box
            marginTop={'auto'}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            py={dense ? 1 : 2}
            px={dense ? 1 : 2}
            flexDirection={{
                xs: 'column',
                md: 'row'
            }}
            gap={dense ? 1 : 2}
        >
            <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                siblingCount={0}
                boundaryCount={1}
                sx={{
                    ...(dense && {
                        '& .MuiPaginationItem-root': {
                            fontSize: '0.75rem',
                            minWidth: '24px',
                            height: '24px',
                            margin: '0 1px'
                        }
                    })
                }}
                renderItem={item => (
                    <PaginationItem
                        {...item}
                        disabled={
                            item.type === 'end-ellipsis' ||
                            item.type === 'start-ellipsis' ||
                            (item.type === 'previous' && currentPage === 1) ||
                            (item.type === 'next' && currentPage === totalPages)
                        }
                    />
                )}
            />
            <Box display="flex" alignItems="center">
                <Typography
                    variant={dense ? "caption" : "body2"}
                    mr={dense ? 0.5 : 1}
                    sx={{
                        color: theme.palette.mode === 'dark' ? '#fff' : '#242333',
                        fontSize: dense ? '0.75rem' : undefined
                    }}
                >
                    Éléments par page:
                </Typography>
                <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    variant="outlined"
                    size={dense ? "small" : "small"}
                    sx={{
                        ...(dense && {
                            minHeight: '24px',
                            '& .MuiSelect-select': {
                                py: 0.5,
                                fontSize: '0.75rem'
                            }
                        })
                    }}
                >
                    {pageSizeOptions.map(count => (
                        <MenuItem key={count} value={count}>
                            <Typography
                                variant={dense ? "caption" : "body2"}
                                sx={{
                                    color: theme.palette.mode === 'dark' ? '#fff' : '#0c0d18',
                                    fontSize: dense ? '0.75rem' : undefined
                                }}
                            >
                                {count}
                            </Typography>
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        </Box>
    );
};

export default DataTablePagination;
